import React from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'

import { UpdatedBadge } from 'features/trend-detection/components/insight-detail-styles'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import { trendDetectionAgentConfigPath } from 'features/trend-detection/paths'

export interface IssueAgentMetaRowProps {
  projectId: string | null
  agentUuid: string | null | undefined
  agentName: string | null | undefined
  /** Formatted detection-period label (e.g. "Detected Apr 5 - Apr 12, 2026"). */
  periodLabel: string | null
  /** When set, renders an "Updated <date>" badge after the meta row. */
  updatedDateLabel?: string | null
  /** Fires before navigation when the agent name link is clicked (e.g. Mixpanel). */
  onAgentNameClick?: () => void
}

export interface TrendLineIconProps {
  width?: number
  height?: number
}

export const TrendLineIcon: React.FC<TrendLineIconProps> = ({
  width = 14,
  height = 12,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 20 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <polyline
      points="1,9 5,9 7,3 10,12 13,4 16,9 20,9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Metadata line: agent name · detection period [Updated <date>].
 */
export const IssueAgentMetaRow: React.FC<IssueAgentMetaRowProps> = ({
  projectId,
  agentUuid,
  agentName,
  periodLabel,
  updatedDateLabel,
  onAgentNameClick,
}) => {
  const hasPeriod = Boolean(periodLabel)
  const hasAgent = Boolean(agentName?.trim())
  if (!hasPeriod && !hasAgent && !updatedDateLabel) return null

  const agentEl =
    projectId && agentUuid && agentName ? (
      <AgentLink
        to={trendDetectionAgentConfigPath(projectId, agentUuid)}
        onClick={() => onAgentNameClick?.()}
      >
        {agentName}
      </AgentLink>
    ) : hasAgent ? (
      <AgentText>{agentName}</AgentText>
    ) : null

  return (
    <MetaRow>
      {agentEl}
      {agentEl && hasPeriod && <DotSeparator aria-hidden>·</DotSeparator>}
      {hasPeriod && <PeriodText>{periodLabel}</PeriodText>}
      {updatedDateLabel && (
        <UpdatedBadge>
          <TrendLineIcon />
          Updated {updatedDateLabel}
        </UpdatedBadge>
      )}
    </MetaRow>
  )
}

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 1.4;
`

const PeriodText = styled.span`
  color: #666;
`

const DotSeparator = styled.span`
  color: #999;
  user-select: none;
`

const AgentLink = styled(Link)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const AgentText = styled.span`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
`
